import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { useCart } from "../context/CartContext";

const FREE_SHIPPING = 150;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty, size: i.size })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-drawer-overlay"
            className="fixed inset-0 bg-black/70 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 h-full w-full max-w-md bg-[#121215] border-l border-zinc-800 z-[70] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-800">
              <p className="font-display font-bold uppercase tracking-tight">Your Bag</p>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-[#FF334B] transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <p data-testid="cart-empty-message" className="text-zinc-500 text-sm mt-10 text-center">
                  Your bag is empty. The archive awaits.
                </p>
              ) : (
                items.map((i) => (
                  <div key={`${i.slug}-${i.size}`} data-testid={`cart-item-${i.slug}`} className="flex gap-4 py-4 border-b border-zinc-800/60">
                    <img src={i.image} alt={i.name} className="w-20 h-24 object-cover bg-zinc-900" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{i.name}</p>
                      <p className="mono text-[10px] tracking-[0.2em] text-zinc-500 mt-1 uppercase">Size {i.size}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <button data-testid={`qty-minus-${i.slug}`} onClick={() => updateQty(i.slug, i.size, -1)} className="border border-zinc-700 p-1 hover:border-[#FF334B] transition-colors"><Minus size={12} /></button>
                        <span className="mono text-xs w-4 text-center">{i.qty}</span>
                        <button data-testid={`qty-plus-${i.slug}`} onClick={() => updateQty(i.slug, i.size, 1)} className="border border-zinc-700 p-1 hover:border-[#FF334B] transition-colors"><Plus size={12} /></button>
                      </div>
                    </div>
                    <div className="text-right flex flex-col justify-between">
                      <p className="mono text-sm">${(i.price * i.qty).toFixed(0)}</p>
                      <button data-testid={`remove-item-${i.slug}`} onClick={() => remove(i.slug, i.size)} className="mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-[#FF334B] uppercase transition-colors">
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-zinc-800 px-6 py-5">
                <div className="mb-4">
                  <p className="mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-2">
                    {subtotal >= FREE_SHIPPING ? "Free shipping unlocked" : `$${(FREE_SHIPPING - subtotal).toFixed(0)} away from free shipping`}
                  </p>
                  <div className="h-1 bg-zinc-800">
                    <div className="h-1 bg-[#FF334B] transition-all" style={{ width: `${Math.min(100, (subtotal / FREE_SHIPPING) * 100)}%` }} />
                  </div>
                </div>
                <div className="flex justify-between mb-4">
                  <span className="mono text-xs tracking-[0.2em] text-zinc-400 uppercase">Subtotal</span>
                  <span data-testid="cart-subtotal" className="mono text-sm">${subtotal.toFixed(0)}</span>
                </div>
                <button
                  data-testid="checkout-button"
                  onClick={checkout}
                  disabled={loading}
                  className="w-full bg-[#FF334B] text-white py-4 mono text-xs tracking-[0.25em] uppercase hover:bg-[#d92339] transition-colors disabled:opacity-50"
                >
                  {loading ? "Redirecting…" : "Proceed to Checkout"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
